import React from 'react';

import type { ColumnMeta, ColumnType, TableVerticalAlign } from '../types';
import TableCell from './TableCell';
import { tableRowVariants } from './tableStyles';

type TableRowProps<T> = {
  row: T;
  rowIndex: number;
  columns: ColumnMeta<T>[];
  clampCells?: boolean;
  cellsClassName?: string;
  cellsContentClassName?: string;
  verticalAlign: TableVerticalAlign;
  isLastRow?: boolean;
  onRowClick?: (row: T) => void;
  rowProps?: React.HTMLAttributes<HTMLTableRowElement>;
  getCellProps?: (
    row: T,
    column: ColumnType<T>,
    rowIndex: number,
    columnIndex: number
  ) => React.TdHTMLAttributes<HTMLTableCellElement>;
};

const TableRow = <T,>({
  row,
  rowIndex,
  columns,
  clampCells,
  cellsClassName,
  cellsContentClassName,
  verticalAlign,
  isLastRow,
  onRowClick,
  rowProps,
  getCellProps,
}: TableRowProps<T>) => {
  const {
    className: externalClassName,
    onClick: externalOnClick,
    ...restProps
  } = rowProps ?? {};

  return (
    <tr
      {...restProps}
      onClick={event => {
        externalOnClick?.(event);
        if (event.defaultPrevented) return;
        event.stopPropagation();
        onRowClick?.(row);
      }}
      className={tableRowVariants({
        clickable: Boolean(onRowClick),
        class: [cellsClassName, externalClassName],
      })}
    >
      {columns.map(column => (
        <TableCell
          key={column.id ?? column.columnIndex}
          row={row}
          rowIndex={rowIndex}
          column={column}
          clampCells={clampCells}
          cellsContentClassName={cellsContentClassName}
          verticalAlign={verticalAlign}
          isLastRow={isLastRow}
          cellProps={getCellProps?.(row, column, rowIndex, column.columnIndex)}
        />
      ))}
    </tr>
  );
};

export default React.memo(TableRow) as typeof TableRow;
